import { useEffect, useState } from 'react';
import { Star, Trash2, Undo2, Plus } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { api } from '../../lib/api';
import { calcularAjuste, numeroDoCampo } from './ajuste-estoque-conta';
import type { LocalProduto } from './LocalizacoesProduto';

// AJUSTAR ESTOQUE PELA FICHA DO PRODUTO ([DA-602]).
//
// Ate aqui o `Est. Atual` da ficha era, pro estoquista, um campo cinza com um aviso amarelo ao lado
// dizendo que so o admin altera ([DA-595]). O aviso estava MENTINDO: o JOAO move o estoque 1.544
// vezes por mes pelos campinhos de prateleira do topo da mesma tela, e a [DA-597] ja tinha aberto o
// ajuste pra ele — so que numa OUTRA tela (a Conferencia dos Vendidos).
//
// Pedido dele, 2026-08-21: *"Ao clicar em Est. Atual, o sistema abre uma tela para o usuario alterar
// a quantidade... Ao lado direito da tela, exibir as Localizacoes, com possibilidade de adicionar,
// modificar ou excluir. As quantidades tem que ser coerentes com Est. Atual."*
//
// ⚠️ Esta tela NAO fura a trava da [DA-595]. Ela fala com a MESMA porta da Conferencia
// (`POST /api/estoque/conferencia/:id/conferir`), que ja exige motivo, ja avisa o admin e ja carimba
// a Ultima Conferencia. Abrir um segundo caminho pra mexer no total seria reabrir o buraco que ele
// mandou fechar no dia 20.
//
// ─── AS DUAS DECISOES DELE SOBRE ESTA TELA (2026-08-21) ────────────────────────────────────────
// 1. *"Nao tem problema em apagar uma prateleira com mercadoria, desde que esteja coerente com campo
//    Est. Atual"* — apagar leva o saldo junto e o TOTAL CAI. A coerencia e' soma == total, nao o
//    total ficar parado.
// 2. *"Sim, tira da fila"* — ajustar por aqui carimba `conferido_em` igual a Conferencia. Some o
//    limite documentado no `contado-em.service` (pela ficha o produto nao saia da fila), porque com
//    motivo obrigatorio o numero igual deixa de ser ambiguo entre "recontei" e "salvei a descricao".
//
// ⚠️ O campo "em qual prateleira" NAO existe, e a ausencia dele e' uma decisao: o mockup tinha um
// combo pra escolher o destino do valor e ele cortou — *"Nao entendi pra que este campo? Se ele
// aparece do lado direito"*. Dois controles pra escolher a mesma prateleira e' a confissao de que a
// fonte nao foi escolhida ([DA-600]). Quem escolhe e' o CLIQUE na linha, como no Padrao de Grid.
//
// ─── [DA-612]: NADA SE CALCULA ENQUANTO ELE DIGITA (2026-08-25) ────────────────────────────────
// *"Gostaria que os campos nao fossem auto-calculados. Gostaria que os calculos fossem realizados
// na hora de salvar"* + *"Se houver inconsistencia, exibir mensagem de erro e cancelar o Salvar"*.
//
// Caiu o espelhamento ao vivo: o `Valor` nao empurra mais a prateleira marcada tecla a tecla, e
// digitar na prateleira nao empurra mais de volta o `Valor` nem troca o botao Adicionar/Diminuir.
// A conciliacao inteira mora no `calcularAjuste`, peca pura chamada no clique ([DA-591] manda o
// pedaco que decide numero ser testavel sem tela).
//
// ⚠️ TRES BLOCOS SAIRAM DA TELA, e a remocao e' consequencia direta da regra, nao faxina: `Total
// atualizado`, o rodapinho `de X para Y · +N × custo = R$` e a caixa `Soma das prateleiras / ✓
// batem` so sabiam falar do RESULTADO — e resultado deixou de existir antes do clique. Manter
// qualquer um deles exibindo o numero velho sob o rotulo "atualizado" seria um numero mentindo
// ([CLAUDE.md raiz] § Codigo: numero grande sem rotulo certo afirma outra coisa).
//
// ⚠️ Achado ao remover, que vale registrar: a caixa `✓ batem` NUNCA mediu nada. Ela imprimia
// `fmt(soma)` nas DUAS linhas — soma das prateleiras e Est. Atual do produto eram a mesma variavel,
// entao ela bateria com qualquer dado. Era enfeite com cara de prova, o caso do `CLAUDE.md` raiz
// § Metodo: *campo que so pode dar um valor nao mede nada, e e' pior que sonda nenhuma*.
//
// ⚠️ O que FICOU e por que nao e' calculo: `Quantidade atual` e' fato do banco (nao deriva de nada
// que ele digitou) e `→ cai na G08 · vitrine` e' a leitura da linha que ELE marcou no clique.

export interface AjusteResultado {
  estoque_atual: string;
  locais: LocalProduto[];
}

interface Props {
  produtoId: number;
  codigo: string;
  descricao: string;
  unidade: string;
  estoqueAtual: string;
  locais: LocalProduto[];
  onFechar: () => void;
  onSalvo: (r: AjusteResultado) => void;
}

interface Linha {
  localizacao_id: number;
  codigo: string;
  descricao?: string | null;
  /**
   * O que esta ESCRITO no campo — texto, nao numero ([DA-612]).
   *
   * ⚠️ Guardar `number` aqui devolveria o auto-calculo pela porta dos fundos: `'1.'` viraria `1`
   * no meio da digitacao e o campo pularia sozinho embaixo do dedo dele.
   */
  quantidade: string;
  minima: string;
  principal: boolean;
  /** Quanto tinha quando a tela abriu — e' o que distingue "ele mexeu aqui" de "esta como veio". */
  original: number;
}

interface Apagada {
  localizacao_id: number;
  codigo: string;
  quantidade: number;
  minima: string;
  principal: boolean;
  original: number;
}

/**
 * `'3.000'` -> `'3'` pra CAMPO DE DIGITACAO.
 *
 * ⚠️ Nao e' cosmetica: o Postgres devolve `numeric` com as 3 casas, e no campo de 74px `20.000`
 * renderiza **truncado** (`20,0`) — o operador le um minimo que nao existe. Visto so na tela
 * publicada; typecheck, os 994 testes e o smoke passaram verdes por cima disso.
 */
const semZeroAtoa = (v: string | null | undefined) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};
/**
 * Igual ao `semZeroAtoa`, mas prateleira NUNCA abre em branco: `null` vira `'0'`.
 *
 * ⚠️ Achado na tela publicada, nao no typecheck ([DA-612]): ao trocar a quantidade de `number` pra
 * texto, prateleira com `quantidade` NULL — que existe, e' o lugar nunca contado — passou a abrir
 * com o campo VAZIO, quando antes o `Number(null)` a mostrava como `0`. Sozinho ja seria a
 * armadilha da [DA-608] (vazio indistinguivel de preenchido); pior que isso, a faixa da recusa
 * conta o mesmo campo como `0`, entao a tela **afirmava duas coisas sobre o mesmo dado** na mesma
 * janela. O `—` do Est. MINIMO ao lado segue certo: minimo em branco e' ausencia legitima.
 */
const qtdDoCampo = (v: string | null | undefined) => semZeroAtoa(v) || '0';
const fmt = (n: number) => Number(n.toFixed(3)).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

export function ModalAjustarEstoque({
  produtoId, codigo, descricao, unidade, estoqueAtual, locais, onFechar, onSalvo,
}: Props) {
  const base = numeroDoCampo(estoqueAtual);

  // O cadastro de prateleiras so e' lido quando o modal ABRE — a ficha do produto nao paga esta
  // consulta em toda edicao so pra o caso de alguem querer acrescentar um lugar.
  const [opcoesLugares, setOpcoesLugares] = useState<OpcaoCombo[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const lista = await api.get<{ id: number; codigo: string; descricao: string | null }[]>('/api/localizacoes');
        setOpcoesLugares(lista.map((l) => ({ value: l.id, label: l.descricao ? `${l.codigo} — ${l.descricao}` : l.codigo })));
      } catch { /* sem lista o combo so nao oferece nada; o resto do modal segue funcionando */ }
    })();
  }, []);

  const [linhas, setLinhas] = useState<Linha[]>(() =>
    [...locais]
      .sort((a, b) => Number(b.principal) - Number(a.principal) || (a.codigo ?? '').localeCompare(b.codigo ?? ''))
      .map((l) => ({
        localizacao_id: l.localizacao_id,
        codigo: l.codigo ?? `#${l.localizacao_id}`,
        descricao: l.descricao,
        quantidade: qtdDoCampo(l.quantidade),
        minima: semZeroAtoa(l.quantidade_minima),
        principal: !!l.principal,
        original: numeroDoCampo(l.quantidade),
      })),
  );
  const [apagadas, setApagadas] = useState<Apagada[]>([]);
  const [alvo, setAlvo] = useState(0);
  const [sinal, setSinal] = useState<1 | -1>(1);
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [adicionando, setAdicionando] = useState<string | number | ''>('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [estrelaMudou, setEstrelaMudou] = useState<number | null>(null);
  /** A contradicao entre os dois lados, achada no clique. Enquanto ha uma, nada foi gravado. */
  const [recusa, setRecusa] = useState<{ esquerda: number; direita: number; diferenca: number } | null>(null);

  const multi = linhas.length > 1;

  // ⚠️ [DA-612] — os quatro gestos abaixo mexem NUM campo so, cada um. Qualquer `set` a mais aqui
  // e' o auto-calculo voltando: era exatamente assim que o `Valor` empurrava a prateleira e a
  // prateleira empurrava o `Valor` de volta, trocando ate o botao Adicionar/Diminuir sozinho.
  function trocarSinal(s: 1 | -1) { setSinal(s); setRecusa(null); }
  function trocarValor(v: string) { setValor(v); setRecusa(null); }
  function marcarAlvo(i: number) { setAlvo(i); setRecusa(null); }

  function editarQuantidade(i: number, v: string) {
    setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, quantidade: v } : l)));
    setRecusa(null);
  }

  function editarMinima(i: number, v: string) {
    setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, minima: v } : l)));
  }

  // ⚠️ A estrela nunca fica vaga: apagar a vitrine passa o posto adiante e a linha DIZ isso. Produto
  // sem estrela nao tem onde o estorno do caixa voltar ([DA-601]).
  function apagar(i: number) {
    const l = linhas[i];
    if (!l) return;
    const resto = linhas.filter((_, k) => k !== i);
    if (l.principal && resto.length > 0 && resto[0]) {
      resto[0] = { ...resto[0], principal: true };
      setEstrelaMudou(resto[0].localizacao_id);
    }
    setApagadas((a) => [...a, { ...l, quantidade: l.original }]);
    setLinhas(resto);
    // ⚠️ [DA-612] So o ALVO se mexe, porque o indice da linha marcada deixou de existir. O `Valor`
    // e o botao ficam como ele os deixou: apagar prateleira COM `Valor` escrito e' contradicao
    // legitima, e quem a nomeia e' a recusa do Salvar — nao a tela limpando campo dele por conta.
    setAlvo(0);
    setRecusa(null);
  }

  function desfazerApagar(id: number) {
    const a = apagadas.find((x) => x.localizacao_id === id);
    if (!a) return;
    setApagadas((xs) => xs.filter((x) => x.localizacao_id !== id));
    setLinhas((ls) => {
      const volta: Linha = { ...a, quantidade: qtdDoCampo(String(a.original)) };
      // Se ela era a vitrine, retoma o posto e o aviso da migracao some.
      if (a.principal) { setEstrelaMudou(null); return [volta, ...ls.map((l) => ({ ...l, principal: false }))]; }
      return [...ls, volta];
    });
    setRecusa(null);
  }

  function adicionarLugar(id: string | number | '') {
    if (id === '' || id === null) return;
    const n = Number(id);
    if (linhas.some((l) => l.localizacao_id === n)) return;
    const opt = opcoesLugares.find((o) => Number(o.value) === n);
    const rotulo = String(opt?.label ?? `#${n}`);
    setLinhas((ls) => [...ls, {
      localizacao_id: n,
      codigo: rotulo.split(' — ')[0] ?? rotulo,
      descricao: rotulo.includes(' — ') ? rotulo.split(' — ')[1] : null,
      quantidade: '0',
      minima: '',
      principal: ls.length === 0,
      original: 0,
    }]);
    setAdicionando('');
  }

  const marcarPrincipal = (i: number) => {
    setLinhas((ls) => ls.map((l, k) => ({ ...l, principal: k === i })));
    setEstrelaMudou(null);
  };

  const usados = new Set(linhas.map((l) => l.localizacao_id));
  const listaCombo = opcoesLugares.map((o) => (usados.has(Number(o.value))
    ? { ...o, desabilitada: true, nota: 'ja esta aqui' }
    : o));
  const temEscolhivel = listaCombo.some((o) => !o.desabilitada);

  // ⚠️ Os DOIS lados normalizados, num lugar so. O banco devolve `'3.000'` e a tela edita `'3'` —
  // comparar cru acusaria mudanca em toda abertura e o Salvar nasceria liberado sem ninguem mexer
  // em nada (mesma armadilha do uppercase por CSS, § Codigo do `sistema/CLAUDE.md`).
  // ⚠️ [DA-612] Antes bastava `delta !== 0` porque tudo era espelho da soma. Com os lados soltos, o
  // `Valor` escrito e a quantidade digitada sao mudancas SEPARADAS e cada uma precisa constar aqui
  // — senao ele digita so de um lado e o Salvar nasce morto dizendo "nada mudou".
  const mudou = numeroDoCampo(valor) !== 0
    || apagadas.length > 0
    || linhas.some((l) => numeroDoCampo(l.quantidade) !== l.original)
    || linhas.some((l) => semZeroAtoa(l.minima) !== semZeroAtoa(locais.find((x) => x.localizacao_id === l.localizacao_id)?.quantidade_minima))
    || linhas.some((l) => !locais.some((x) => x.localizacao_id === l.localizacao_id))
    || linhas.some((l) => l.principal !== !!locais.find((x) => x.localizacao_id === l.localizacao_id)?.principal);

  // Botao desabilitado DIZ por que — beco sem saida nao e' "interface limpa".
  const bloqueio = !motivo.trim()
    ? 'Escreva o comentário para poder salvar'
    : !mudou ? 'Nada mudou — nada a salvar' : '';

  async function salvar() {
    if (bloqueio || salvando) return;

    // ⚠️ [DA-612] AQUI e' o unico lugar onde a conta acontece. Ela roda ANTES de qualquer coisa
    // sair da maquina: contradicao entre os lados cancela o Salvar sem tocar a rede.
    const conta = calcularAjuste({ base, sinal, valor, alvo, linhas, apagadas });
    if (!conta.ok) {
      setRecusa({ esquerda: conta.esquerda, direita: conta.direita, diferenca: conta.diferenca });
      setErro('');
      return;
    }
    setRecusa(null);

    const finais = linhas.map((l) => ({
      ...l,
      quantidade: conta.quantidades.get(l.localizacao_id) ?? numeroDoCampo(l.quantidade),
    }));

    setSalvando(true);
    setErro('');
    try {
      await api.post(`/api/estoque/conferencia/${produtoId}/conferir`, {
        estoque_atual: conta.total,
        motivo: motivo.trim(),
        lugares: finais.map((l) => ({
          localizacao_id: l.localizacao_id,
          quantidade: l.quantidade,
          quantidade_minima: l.minima.trim() === '' ? null : numeroDoCampo(l.minima),
          principal: l.principal,
        })),
      });
      onSalvo({
        estoque_atual: String(conta.total),
        locais: finais.map((l) => ({
          localizacao_id: l.localizacao_id,
          principal: l.principal,
          quantidade: String(l.quantidade),
          quantidade_minima: l.minima.trim() === '' ? null : l.minima,
          codigo: l.codigo,
          descricao: l.descricao,
        })),
      });
    } catch (e: any) {
      // A recusa do backend vem em pt-BR (`detalhe`) e precisa CHEGAR na tela ([DA-547]).
      setErro(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel salvar o ajuste.');
      setSalvando(false);
    }
  }

  const grade = '24px 1fr 74px 74px 30px';
  const rot: React.CSSProperties = {
    fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', opacity: .65, fontWeight: 700,
  };
  const campoNum: React.CSSProperties = {
    width: '100%', textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', borderRadius: 6, fontSize: 13,
  };

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Ajustar estoque"
      largura={1060}
      bodyStyle={{ overflow: 'hidden', padding: 0 }}
      rodape={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: erro ? '#b91c1c' : bloqueio ? '#b91c1c' : 'transparent' }}>
            {erro || bloqueio || '.'}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={onFechar} className="btn-secundario">Cancelar</button>
            <button type="button" onClick={salvar} disabled={!!bloqueio || salvando} className="btn-primario">
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--page-bg)', flex: '0 0 auto',
          display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, color: 'var(--num-destaque)' }}>{codigo}</span>
          <span style={{ fontWeight: 600 }}>{descricao}</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, opacity: .7 }}>unidade de venda: {unidade}</span>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          flex: '1 1 auto', minHeight: 0,
        }}>
          {/* ─── ESQUERDA: a quantidade ─────────────────────────────────────────────────────── */}
          <div style={{ padding: 16, overflowY: 'auto', borderRight: '1px solid var(--page-bg)' }}>
            <div style={rot}>Quantidade atual</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
              <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{fmt(base)}</span>
              <span style={{ fontSize: 13, opacity: .7 }}>{unidade}</span>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {([[1, '+ Adicionar'], [-1, '− Diminuir']] as const).map(([s, r]) => (
                <button key={s} type="button" onClick={() => trocarSinal(s)}
                  style={{
                    flex: 1, padding: 10, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14,
                    border: '1px solid', borderColor: sinal === s ? 'var(--primary)' : 'var(--input-border)',
                    background: sinal === s ? 'var(--primary)' : 'var(--input-bg)',
                    color: sinal === s ? '#fff' : 'var(--input-fg)',
                  }}>{r}</button>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={rot}>Valor</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input type="number" min="0" step="0.001" value={valor} onChange={(e) => trocarValor(e.target.value)}
                  style={{
                    width: 130, fontFamily: '"IBM Plex Mono", monospace', fontSize: 20, fontWeight: 700,
                    textAlign: 'right', borderRadius: 8,
                  }} />
                <span style={{ fontSize: 13, opacity: .7 }}>{unidade}</span>
              </div>
              {/* Onde o valor cai — leitura da linha que ELE marcou, nao uma conta ([DA-612]). */}
              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: 'var(--primary)', minHeight: 16 }}>
                {apagadas.length > 0 ? (() => {
                  const a = apagadas[apagadas.length - 1];
                  return a ? `→ ${fmt(a.quantidade)} saíram junto com a ${a.codigo} apagada` : '';
                })() : numeroDoCampo(valor) !== 0 && linhas[alvo] ? (
                  <>
                    → cai na <b>{linhas[alvo]?.codigo}</b>{linhas[alvo]?.principal ? ' · vitrine' : ''}
                    {multi && <span style={{ opacity: .7, fontWeight: 600 }}> (clique noutra prateleira para mudar)</span>}
                  </>
                ) : ' '}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--page-bg)', margin: '12px 0' }} />

            <div style={{ marginTop: 12 }}>
              <div style={rot}>Comentário <span style={{ color: '#b91c1c' }}>*</span></div>
              <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="POR QUE O NUMERO MUDOU?"
                style={{ width: '100%', marginTop: 4, borderRadius: 8, resize: 'none' }} />
            </div>
          </div>

          {/* ─── DIREITA: as prateleiras ────────────────────────────────────────────────────── */}
          <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700 }}>Localizações</span>
              <span style={{ fontSize: 11, opacity: .6 }}>{linhas.length === 1 ? '1 lugar' : `${linhas.length} lugares`}</span>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: grade, gap: 6, marginTop: 10, padding: '0 6px',
              fontSize: 10, letterSpacing: '.04em', textTransform: 'uppercase', opacity: .6, fontWeight: 700,
            }}>
              <span /><span>Lugar</span>
              <span style={{ textAlign: 'right' }}>Est. Atual</span>
              <span style={{ textAlign: 'right' }}>Est. Mínimo</span><span />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {linhas.map((l, i) => {
                const marcada = multi && i === alvo && numeroDoCampo(valor) !== 0 && apagadas.length === 0;
                return (
                  <div key={l.localizacao_id} onClick={() => marcarAlvo(i)}
                    style={{
                      display: 'grid', gridTemplateColumns: grade, gap: 6, alignItems: 'center',
                      padding: 6, borderRadius: 8, cursor: 'pointer',
                      ...(marcada ? { background: '#fde68a', color: '#000', boxShadow: 'inset 3px 0 0 var(--primary)' } : {}),
                    }}>
                    <button type="button" title={l.principal ? 'Vitrine — a venda baixa daqui' : 'Marcar como vitrine'}
                      onClick={(e) => { e.stopPropagation(); marcarPrincipal(i); }}
                      style={{ background: 'none', border: 0, padding: 0, lineHeight: 0, cursor: 'pointer', color: 'inherit', opacity: l.principal ? 1 : .25 }}>
                      <Star size={15} fill={l.principal ? 'currentColor' : 'none'} />
                    </button>
                    <span style={{ minWidth: 0 }}>
                      <b style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{l.codigo}</b>
                      {l.descricao && <span style={{ opacity: .6, fontSize: 11 }}> {l.descricao}</span>}
                      {estrelaMudou === l.localizacao_id && (
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--primary)' }}> virou a vitrine</span>
                      )}
                    </span>
                    <input type="number" step="0.001" value={l.quantidade} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => editarQuantidade(i, e.target.value)} style={{ ...campoNum, fontWeight: 700 }} />
                    <input type="number" step="0.001" value={l.minima} placeholder="—" onClick={(e) => e.stopPropagation()}
                      onChange={(e) => editarMinima(i, e.target.value)} style={campoNum} />
                    <button type="button" title="Apagar esta prateleira"
                      onClick={(e) => { e.stopPropagation(); apagar(i); }}
                      style={{ background: 'none', border: 0, padding: 0, lineHeight: 0, cursor: 'pointer', color: '#b91c1c' }}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}

              {/* Apagada = ATENCAO (laranja solido + preto), nunca vermelho: vermelho e' erro real,
                  e apagar prateleira e' uma escolha dele. Fica na tela, riscada e com volta. */}
              {apagadas.map((a) => (
                <div key={a.localizacao_id} style={{
                  display: 'grid', gridTemplateColumns: grade, gap: 6, alignItems: 'center',
                  padding: 6, borderRadius: 8, background: '#f97316', color: '#000',
                }}>
                  <span />
                  <span>
                    <b style={{ fontFamily: '"IBM Plex Mono", monospace', textDecoration: 'line-through' }}>{a.codigo}</b>
                    <span style={{ fontSize: 11, fontWeight: 700 }}> apagada</span>
                  </span>
                  <span style={{ textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, textDecoration: 'line-through' }}>
                    {fmt(a.quantidade)}
                  </span>
                  <span />
                  <button type="button" title="Desfazer" onClick={() => desfazerApagar(a.localizacao_id)}
                    style={{ background: 'none', border: 0, padding: 0, lineHeight: 0, cursor: 'pointer', color: '#000' }}>
                    <Undo2 size={15} />
                  </button>
                </div>
              ))}
            </div>

            {temEscolhivel && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <Plus size={14} style={{ color: 'var(--primary)', flex: '0 0 auto' }} />
                <div style={{ flex: 1 }}>
                  <ComboBox opcoes={listaCombo} value={adicionando} onChange={adicionarLugar} placeholder="Adicionar lugar..." />
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ─── A RECUSA ([DA-612]) ──────────────────────────────────────────────────────────────
            So existe DEPOIS do clique, e enquanto ela esta na tela nada foi gravado. Mostra os
            DOIS numeros porque a saida depende de qual deles ele quis: dizer apenas "nao fecha"
            deixaria o operador adivinhando qual lado corrigir ([DA-547] — a recusa tem que chegar
            em quem a encontra, em pt-BR e dizendo o que fazer). */}
        {recusa && (
          <div style={{
            flex: '0 0 auto', margin: '0 16px 12px', padding: '12px 14px', borderRadius: 10,
            border: '1px solid #fca5a5', background: '#fee2e2', color: '#7f1d1d',
          }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, color: '#991b1b' }}>
              ⚠ Os dois lados não fecham — nada foi gravado.
            </div>
            <table style={{ marginTop: 7, borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                <tr>
                  <td style={{ padding: '1px 0' }}>Pelo <b>Valor</b>: {fmt(base)} {sinal > 0 ? '+' : '−'} {fmt(Math.abs(numeroDoCampo(valor)))}</td>
                  <td style={{ padding: '1px 0 1px 12px', textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700 }}>
                    {fmt(recusa.esquerda)} {unidade}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '1px 0' }}>Pelas <b>prateleiras</b>: {linhas.map((l) => fmt(numeroDoCampo(l.quantidade))).join(' + ')}</td>
                  <td style={{ padding: '1px 0 1px 12px', textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700 }}>
                    {fmt(recusa.direita)} {unidade}
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingTop: 4 }}><b>Diferença</b></td>
                  <td style={{ paddingTop: 4, paddingLeft: 12, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700 }}>
                    {fmt(recusa.diferenca)} {unidade}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 12.5 }}>
              Corrija um dos dois lados: apague o <b>Valor</b> para as prateleiras mandarem, ou
              desfaça o que digitou nas quantidades.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
